/**
 * Performs a recusive, deep comparison of two objects.
 */
export default function deepequal(a: any, b: any) {
  if (a === b) {
    return true;
  }

  if (a && b && typeof a === "object" && typeof b === "object") {
    if (a.constructor !== b.constructor) {
      return false;
    }

    let length, i;
    if (Array.isArray(a) || (ArrayBuffer.isView(a) && !(a instanceof DataView))) {
      // TypedArrays all have defined length, but ts thinks a and b might have some other type.
      length = (a as any[]).length;
      if (length !== (b as any[]).length) {
        return false;
      }
      for (i = length; i-- !== 0; ) {
        if (!deepequal((a as any[])[i], (b as any[])[i])) {
          return false;
        }
      }
      return true;
    }

    if (a.constructor === RegExp) {
      return a.source === b.source && a.flags === b.flags;
    }
    if (a.valueOf !== Object.prototype.valueOf) {
      return a.valueOf() === b.valueOf();
    }
    if (a.toString !== Object.prototype.toString) {
      return a.toString() === b.toString();
    }

    const keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) {
      return false;
    }

    for (i = length; i-- !== 0; ) {
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) {
        return false;
      }
    }

    for (i = length; i-- !== 0; ) {
      const key = keys[i];
      if (!deepequal(a[key], b[key])) {
        return false;
      }
    }

    return true;
  }

  // true if both NaN, false otherwise
  return a !== a && b !== b;
}

interface DeepEqualOptions {
  readonly omit?: { [key: string]: boolean };
  readonly ignoreUndefined?: boolean;
  readonly ignoreNullish?: boolean;
}

export function deepequalWithOptions(a: any, b: any, options?: DeepEqualOptions) {
  if (a === b) {
    return true;
  }

  if (a && b && typeof a === "object" && typeof b === "object") {
    if (a.constructor !== b.constructor) {
      return false;
    }

    if (a.constructor === RegExp) {
      return a.source === b.source && a.flags === b.flags;
    }

    const { omit = {}, ignoreUndefined = false, ignoreNullish = false } = options ?? {};

    const aCopy = { ...a };
    const bCopy = { ...b };

    for (const key in omit) {
      delete aCopy[key];
      delete bCopy[key];
    }

    a = aCopy;
    b = bCopy;

    let length, i;
    if (Array.isArray(a) || (ArrayBuffer.isView(a) && !(a instanceof DataView))) {
      // TypedArrays all have defined length, but ts thinks a and b might have some other type.
      length = (a as any[]).length;
      if (length !== (b as any[]).length) {
        return false;
      }
      for (i = length; i-- !== 0; ) {
        if (!deepequalWithOptions((a as any[])[i], (b as any[])[i], options)) {
          return false;
        }
      }
      return true;
    }

    if (a.valueOf !== Object.prototype.valueOf) {
      return a.valueOf() === b.valueOf();
    }
    if (a.toString !== Object.prototype.toString) {
      return a.toString() === b.toString();
    }

    let aKeys = ignoreUndefined ? Object.keys(a).filter((key) => a[key] !== undefined) : Object.keys(a);
    let bKeys = ignoreUndefined ? Object.keys(b).filter((key) => b[key] !== undefined) : Object.keys(b);

    if (ignoreNullish) {
      aKeys = aKeys.filter(
        (key) =>
          a[key] !== undefined &&
          a[key] !== null &&
          !(typeof a[key] === "string" && a[key] === "") &&
          !(typeof a[key] === "boolean" && a[key] === false),
      );
      bKeys = bKeys.filter(
        (key) =>
          b[key] !== undefined &&
          b[key] !== null &&
          !(typeof b[key] === "string" && b[key] === "") &&
          !(typeof b[key] === "boolean" && b[key] === false),
      );
    }

    length = aKeys.length;
    if (length !== bKeys.length) {
      return false;
    }

    for (i = length; i-- !== 0; ) {
      if (!Object.prototype.hasOwnProperty.call(b, aKeys[i])) {
        return false;
      }
    }

    for (i = length; i-- !== 0; ) {
      const key = aKeys[i];
      if (!deepequalWithOptions(a[key], b[key], options)) {
        return false;
      }
    }

    return true;
  }

  // true if both NaN, false otherwise
  return a !== a && b !== b;
}
